import { getEntitySetValueFields } from "../data/mcaEntities";
import { getMcaEntityFormSchema } from "../components/mca/mcaEntityFormRegistry";

export const NOTIFICATION_RENEWAL_STATUS_OPTIONS = [
  { value: "DFT", label: "Draft" },
  { value: "PENDING", label: "Pending" },
  { value: "WAIT", label: "Wait" },
  { value: "CONFIRM", label: "Confirmed" },
  { value: "TERMINATE", label: "Terminated" },
];

const NOTIFICATION_RENEWAL_SET_VALUE_FIELDS = [
  {
    field: "status",
    label: "Status",
    options: NOTIFICATION_RENEWAL_STATUS_OPTIONS,
  },
];

export const BOOLEAN_WATCH_VALUE_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

const KNOWN_BOOLEAN_WATCH_FIELDS = {
  client: [
    "identityExpired",
    "isTransfer",
    "vulnerable",
    "pep.isPep",
    "professionalInvestor",
    "knowledgeOfDerivatives",
  ],
  order: [
    "basicOrder",
    "forceUncertainPayment",
    "isCertainPayment",
    "isGI",
    "isRider",
    "isSplit",
    "mainlandChineseVisitor",
    "policyReplacement",
    "premiumFinancing",
    "professionalInvestor",
    "taxable",
  ],
  debitNote: ["forceRenewal", "taxable"],
  renewal: ["fromSystem", "fromAI"],
};

const getComponentName = (component) => {
  if (!component) return "";
  if (typeof component === "string") return component;
  return component.displayName || component.name || "";
};

const isBooleanFieldComponent = (component) =>
  /^(Switch|Checkbox)$/i.test(getComponentName(component));

const isSelectFieldComponent = (component) =>
  /Select$/i.test(getComponentName(component));

export const normalizeSelectOptions = (data) => {
  if (!Array.isArray(data) || !data.length) return null;
  const options = data
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return { value: String(item), label: String(item) };
      }
      if (item && typeof item === "object") {
        const value = item.value ?? item.id;
        if (value == null || value === "") return null;
        const label = item.label ?? String(value);
        return { value: String(value), label: String(label) };
      }
      return null;
    })
    .filter(Boolean);
  return options.length ? options : null;
};

export const extractSchemaFieldOptions = (schema, fieldPath) => {
  const field = (schema?.layout?.fields ?? []).find((item) => item?.name === fieldPath);
  if (!field) return null;

  if (isBooleanFieldComponent(field.component)) {
    return BOOLEAN_WATCH_VALUE_OPTIONS;
  }

  if (isSelectFieldComponent(field.component) || Array.isArray(field.props?.data)) {
    return normalizeSelectOptions(field.props?.data);
  }

  return null;
};

export const isKnownBooleanWatchField = (entity, field) =>
  (KNOWN_BOOLEAN_WATCH_FIELDS[entity] ?? []).includes(field);

export const getNotificationWatchFieldValueOptions = (entity, field) => {
  if (!entity || !field) return null;

  const setValueFields =
    entity === "renewal"
      ? NOTIFICATION_RENEWAL_SET_VALUE_FIELDS
      : getEntitySetValueFields(entity);
  const setValueDef = setValueFields.find((item) => item.field === field);
  if (setValueDef?.options?.length) return setValueDef.options;

  const schema = getMcaEntityFormSchema(entity, (key) => key);
  const schemaOptions = extractSchemaFieldOptions(schema, field);
  if (schemaOptions?.length) return schemaOptions;

  if (isKnownBooleanWatchField(entity, field)) {
    return BOOLEAN_WATCH_VALUE_OPTIONS;
  }

  return null;
};

export const isBooleanWatchValueOptions = (options) =>
  Array.isArray(options) &&
  options.length === BOOLEAN_WATCH_VALUE_OPTIONS.length &&
  BOOLEAN_WATCH_VALUE_OPTIONS.every((option) =>
    options.some(
      (candidate) =>
        candidate.value === option.value && candidate.label === option.label
    )
  );

export const coerceWatchFieldValueForSelect = (value, options) => {
  if (!isBooleanWatchValueOptions(options)) {
    return value ?? "";
  }
  if (value === true || value === "true") return "true";
  if (value === false || value === "false") return "false";
  return value ?? "";
};
